import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import '@/index.admin.css';
import AdminPwaHead from '@/components/admin/AdminPwaHead';
import AdminAmbientShell from '@/components/admin/AdminAmbientShell';
import StaffSmsConsentFields from '@/components/admin/StaffSmsConsentFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminAcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [inviteUser, setInviteUser] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [activated, setActivated] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setValid(false);
      return;
    }
    axios
      .get(`${BACKEND_URL}/api/admin/invite/validate`, { params: { token } })
      .then((res) => {
        setValid(true);
        setInviteUser(res.data.user);
        setName(res.data.user?.name || '');
      })
      .catch((err) => {
        setValid(false);
        toast.error(err.response?.data?.detail || 'Invite is invalid or expired');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (!acceptedTerms) {
      toast.error('Please accept the Terms and Conditions and Privacy Policy');
      return;
    }
    if (smsOptIn && !phone.trim()) {
      toast.error('Mobile phone is required to receive SMS notifications');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/invite/accept`, {
        token,
        password,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        sms_opt_in: smsOptIn,
        accepted_terms: acceptedTerms,
      });
      setActivated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminAmbientShell className="items-center justify-center px-4">
      <AdminPwaHead />
      <button
        onClick={() => navigate('/')}
        className="absolute z-20 top-[max(1.5rem,env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))] min-h-11 text-white/60 hover:text-white transition-colors flex items-center gap-2 px-2"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-['Manrope']">Back to Home</span>
      </button>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.35 }}
        className="relative z-10 w-full max-w-lg"
      >
        <div className="admin-glass-elevated p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 border border-blue-500/20 mb-4">
              {activated || valid ? (
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              ) : (
                <Lock className="w-8 h-8 text-blue-400" />
              )}
            </div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
              {activated ? 'Account activated' : 'Activate Admin Access'}
            </h1>
            <p className="text-white/60 font-['Manrope'] text-sm">
              {loading
                ? 'Validating invite…'
                : activated
                  ? 'Thank you for activating your Drive Now Autohaus account.'
                  : valid
                    ? `Welcome${inviteUser?.email ? `, ${inviteUser.email}` : ''}. Set your password to continue.`
                    : 'This invite link is invalid or has expired.'}
            </p>
          </div>

          {activated && (
            <div className="space-y-4 text-center" data-testid="activation-thank-you">
              <p className="text-sm font-['Manrope'] text-white/70">
                Sign in with your email and new password to open the admin portal.
              </p>
              {inviteUser?.email && (
                <p className="text-sm font-['Manrope'] text-white/45">
                  Email: {inviteUser.email}
                </p>
              )}
              <Link
                to="/admin/login"
                className="block w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold transition-colors"
                data-testid="activation-login-link"
              >
                Go to admin portal login
              </Link>
              <p className="text-sm font-['Manrope']">
                <Link
                  to="/admin/login"
                  className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                >
                  https://www.drivenow54.com/admin/login
                </Link>
              </p>
            </div>
          )}

          {!loading && valid && !activated && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-['Manrope'] text-white/70 mb-1.5">Display name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-['Manrope'] text-white/70 mb-1.5">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 pr-12 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors inline-flex items-center justify-center min-h-11 min-w-11"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-['Manrope'] text-white/70 mb-1.5">Confirm password *</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 pr-12 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors inline-flex items-center justify-center min-h-11 min-w-11"
                    aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <StaffSmsConsentFields
                phone={phone}
                onPhoneChange={setPhone}
                smsOptIn={smsOptIn}
                onSmsOptInChange={setSmsOptIn}
                acceptedTerms={acceptedTerms}
                onAcceptedTermsChange={setAcceptedTerms}
              />
              <button
                type="submit"
                disabled={submitting || !acceptedTerms}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold transition-colors disabled:opacity-50"
              >
                {submitting ? 'Activating…' : 'Set Password & Activate'}
              </button>
            </form>
          )}

          {!loading && !valid && (
            <button
              type="button"
              onClick={() => navigate('/admin/login')}
              className="w-full py-3 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
            >
              Go to Login
            </button>
          )}
        </div>
      </motion.div>
    </AdminAmbientShell>
  );
}
